import { useEffect, useState } from "react";
import UserInteractionsService from "../../services/interactions/Interactions";
import { convertToUTC } from "../../utils/utils";
import useGlobalContext from "../../context/useGlobalContext";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { Box } from "@mui/system";
import Datatable from "../Common/Datatable";
import { CircularProgress } from "@mui/material";
import { getColumns, initialSort } from "./analyticsUserTableConfig";
import { UserTeamData } from "../../utils/types";

const generateRows = (
  meetings: any,
  isTeamMeeting: boolean,
  newUserTeamData: any,
  parent_page_identifier: string,
) =>
  meetings.map((item: any) => ({
    id: item.interactionId,
    title: item.interactionTitle || "",
    description: item.interactionDescription || "",
    start_time: item.startTime,
    end_time: item.endTime,
    duration: item.duration,
    no_of_attendees: item.noOfAttendees,
    is_external: item.isExternal,
    selfTalkTime: item.selfTalkTime,
    accountTalkTime: item.accountTalkTime,
    clientTalkTime: item.clientTalkTime,
    actualDuration: item.actualDuration,

    attendees: item.attendees || {},
    joinLink: item.joinLink || "",
    meeting_sentiment: item.overallSentiment || null,
    rating_source: item.ratingSource || null,
    isTeamMeeting: isTeamMeeting,
    newUserTeamData: newUserTeamData,
    parent_page_identifier: parent_page_identifier,
  }));

const AnalyticsUserInteraction: React.FC<{
  employee_email_ids?: string;
  newUserTeamData: UserTeamData;
  startTime: string;
  endTime: string;
  tableFilters?: any[];
}> = ({
  employee_email_ids,
  newUserTeamData,
  startTime,
  endTime,
  tableFilters,
}) => {
  const [columns] = useState<any[]>(getColumns(employee_email_ids|| ""));
  const [rows, setRows] = useState<any[]>([]);
  const { startEndTime, timeRange } = useGlobalContext();
  const [totalRecord, setTotalRecord] = useState(0);
  const { showSnackbar } = useDisplaySnackbar();
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState(initialSort);
  const isTeamMeeting = true;

  const parent_page_identifier =  "teamInsightsTableConfig";

  const interactionTypeFilter = tableFilters?.find(
    (f) => f.filterBy === "interactionType"
  );
  const isRatedFilter = tableFilters?.find((f) => f.filterBy === "isRated");

  const isExternal = interactionTypeFilter?.searchText === "external";

  let overallSentiment = "";
  if (isRatedFilter?.searchText === "rated") {
    overallSentiment = "rated";
  }

  const {
    isLoading: isInteractionsLoading,
    isFetching: isInteractionsFetching,
    isSuccess: isInteractionsSuccess,
    isError: isInteractionsError,
    error: interactionsError,
    data: totalInteractionsData,
  } = UserInteractionsService.useGetUserInteractions(
    {
      startTime: convertToUTC(startTime),
      endTime: convertToUTC(endTime),
      offset: pageNumber,
      limit: pageSize,
      sortBy: sort.sort_field,
      sortOrder: sort.sort_order,
      isTeamInteraction: false,
      employeePorId:
        employee_email_ids && employee_email_ids.length > 0
          ? employee_email_ids
          : "",
      isExternal,
      overallSentiment,
      isSupportAdditionalCols: true,
    },
    true
  );

  useEffect(() => {
    setShowSpinner(isInteractionsLoading || isInteractionsFetching);
    if (isInteractionsSuccess) {
      const totalInteractionList = totalInteractionsData?.data || [];
      setTotalRecord(totalInteractionsData?.totalRecords);
      if (totalInteractionList.length > 0) {
        setRows(
          generateRows(totalInteractionList, isTeamMeeting, newUserTeamData, parent_page_identifier)
        );
      }
    }
    if (isInteractionsError) {
      showSnackbar(
        "error",
        interactionsError || "Failed to fetch client interactions"
      );
      console.error("Error:", interactionsError);
    }
  }, [
    isInteractionsLoading,
    isInteractionsFetching,
    interactionsError,
    isInteractionsSuccess,
    isInteractionsError,
  ]);

  const onPageChange = (value: number) => {
    setPageNumber(value);
  };

  const onPaginationChange = (pageNumber: number) => {
    onPageChange(pageNumber + 1);
  };

  const onSortingChange = (sorting: any[]) => {
    if (sorting && sorting.length > 0)
      setSort({
        sort_field: sorting[0].id,
        sort_order: sorting[0].desc === true ? "desc" : "asc",
      });
  };
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  return (
    <>
      <Box
        className="relative flex flex-col justify-start items-start w-full"
        sx={{ gap: "12px" }}
      >
        {showSpinner && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 z-10">
            <CircularProgress
              sx={{ color: "#ccc", opacity: 0.5 }}
              thickness={5}
              size={60}
            />
          </div>
        )}
        <Box className="w-full">
          <Datatable
            columns={columns}
            rows={rows}
            rowCount={totalRecord}
            pageSize={pageSize}
            initialSort={sort}
            onPaginationChange={onPaginationChange}
            onSortingChange={onSortingChange}
            startEndTime={startEndTime}
            timeRange={timeRange}
            calendarId={null}
            onPageSizeChange={(newPageSize: number | null | undefined) => {
              if (newPageSize !== null && newPageSize !== undefined) {
                handlePageSizeChange(newPageSize);
              }
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default AnalyticsUserInteraction;
